import React, { useState, useEffect } from 'react';
import TopNavbar from '../../components/TopNavbar/TopNavbar';
import ApiManager from '../../ApiManager/ApiManager';
import { useParams } from 'react-router-dom';
import DriverSalaryInfo from './DriverSalaryInfo';
import './DriverPayroll.css';
import Table from '../../components/Table/Table';
import Payroll from './Payroll';
import { useAuth } from '../../components/AuthProvider';
import PERMISSIONS from '../../permissions';
import { useComponentLoading } from '../../utils/useComponentLoading';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import Modal from '../../components/Modal/Modal';

function DriverPayroll() {

  const [data, setData] = useState([]);
  const [rowsId, serRowsId] = useState(null);
  const [paidSlips, setPaidSlips] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);

  const { driverId } = useParams();

  // Loading state management
  const {
    loadingStates,
    loadingMessages,
    loadingProgress,
    withLoading
  } = useComponentLoading({ driverData: false, paidSlips: false, fileView: false });

  console.log("driver id: ", driverId);


  useEffect(() => {
    fetchData();
    fetchPaidSlips();
  }, [driverId]);

  const fetchData = async () => {
    try {
      await withLoading('driverData', async (updateProgress) => {
        updateProgress(20);
        const query = new URLSearchParams({
          driverId: driverId,
        });
        const response = await ApiManager.getDriver(query);
        updateProgress(60);
        if (!response.ok) throw new Error('Network response was not ok');
        const json = await response.json();
        updateProgress(100);
        setData(json);
      }, 'Loading driver data...', 10000);
    } catch (error) {
      console.log('error', error);
    }
  };

  const fetchPaidSlips = async () => {
    try {
      await withLoading('paidSlips', async (updateProgress) => {
        updateProgress(20);
        const query = new URLSearchParams({
          driver_id: driverId,
        });
        const response = await ApiManager.getPaidSlips(query);
        updateProgress(60);
        if (!response.ok) throw new Error('Network response was not ok');
        const json = await response.json();
        updateProgress(100);
        setPaidSlips(json);
      }, 'Loading paid slips...', 10000);
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleFileClick = async (fileName) => {
    try {
      setIsFileModalOpen(true);
      setFileUrl(null);
      setFileType(null);
      await withLoading('fileView', async (updateProgress) => {
        updateProgress(10);
        // Get a presigned URL for direct download
        const response = await ApiManager.storageGetPresignedDownloadUrl(fileName);
        updateProgress(30);
        if (!response.ok) {
          throw new Error("Failed to generate presigned URL");
        }
        updateProgress(50);
        const result = await response.json();
        updateProgress(70);
        setFileUrl(result.url);
        // Try to determine file type from URL or filename
        const fileExtension = fileName.split('.').pop().toLowerCase();
        let mimeType;
        switch (fileExtension) {
          case 'pdf':
            mimeType = 'application/pdf';
            break;
          case 'jpg':
          case 'jpeg':
            mimeType = 'image/jpeg';
            break;
          case 'png':
            mimeType = 'image/png';
            break;
          case 'html':
            mimeType = 'text/html';
            break;
          default:
            mimeType = 'application/octet-stream';
        }
        setFileType(mimeType);
        updateProgress(100);
      }, 'Loading file...', 30000);
    } catch (error) {
      setIsFileModalOpen(false);
      setFileUrl(null);
      setFileType(null);
    }
  };

  const columns = [
    { field: 'paid_from', headerName: 'Paid From', type: 'date'},
    { field: 'paid_till', headerName: 'Paid Till', type: 'date'},
    { field: 'amount', headerName: 'Amount', type: 'number' },
    {
      field: "file_path",
      headerName: "Receipt",
      renderCell: (params) => {
        if (!params.row.file_path) return "N/A";
        return (
          <button onClick={() => handleFileClick(params.row.file_path)} className="custom-button">
            View Receipt
          </button>
        );
      },
  },
];

const rows = (paidSlips || []).map((item, index) => ({
  id: index + 1,
  driver_id: item.driver_id,
  vendor_id: item.vendor_id || 'N/A',
  paid_from: item.paid_from,
  paid_till: item.paid_till,
  amount: `₹${item.amount}`,
  file_path: item.file_path || null 
}));

  // Show loading spinner if either driver data or paid slips are loading
  if (loadingStates.driverData || loadingStates.paidSlips) {
    return <LoadingSpinner 
      message={loadingStates.driverData ? loadingMessages.driverData : loadingMessages.paidSlips}
      progress={loadingStates.driverData ? loadingProgress.driverData : loadingProgress.paidSlips}
      showProgress={true}
    />;
  }

  return (
    <div style={{paddingBottom:"30px"}}>
      <TopNavbar
        title="Driver Payroll"
      />
      <div style={{display:"flex",gap:"20px",marginTop:"20px"}}>
        <div className='driver-payroll-salary-box'>
          <DriverSalaryInfo driver={data} />
        </div>
        <div className='driver-payroll'>
          <Payroll driverId={driverId} />
        </div>
      </div>
      <Table
        table_columns={columns}
        rows={rows}
        setRowsId={serRowsId}
      />

      {isFileModalOpen && (
        <Modal isOpen={isFileModalOpen} onClose={() => setIsFileModalOpen(false)}>
          {loadingStates.fileView ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '500px',
              width: '100%'
            }}>
              <LoadingSpinner
                overlay={false}
                message={loadingMessages.fileView}
                progress={loadingProgress.fileView}
                showProgress={true}
              />
            </div>
          ) : fileUrl ? (
            <>
              {fileType === "application/pdf" || fileType === "text/html" ? (
                <iframe src={fileUrl} width="100%" height="500px" title="File Preview"></iframe>
              ) : (
                <img
                  src={fileUrl}
                  alt="Receipt"
                  style={{
                    maxWidth: "40%",
                    objectFit: "contain",
                    display: "block",
                    margin: "auto",
                  }}
                />
              )}
            </>
          ) : (
            <p>Loading file...</p>
          )}
        </Modal>
      )}
    </div>
  );
}

export default DriverPayroll;
